import { Box, Alert, AlertTitle, alpha } from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';

import * as Extent from 'ol/extent';
import { useDispatch } from 'react-redux';
import { fromLonLat, toLonLat } from 'ol/proj';
import GeoMapWithTiles from '../../../../features/geo-map/GeoMapWithTiles';
import style from './DeliveryGeoMapEditorWidget.module.css';
import Loading from '../../../../shared/ui/Loading/Loading';
import LineVectorLayerV2 from '../../../../shared/ui/GeoMap/LineVectorLayerV2';
import { useAppSelector } from '../../../../app/store';
import reducerPath from '../../../../app/reducerPath';
import {
  transformData,
  LogisticTasksEnum,
  logisticTasksPetrolResourceActions,
} from '../../../../entities/logistic/logisticTasks/redux/logisticTasksPetrol.slice';
import { DEFAULT_EXTENT } from '../../../../features/geo-map/GeoMap.constants';
import Marker from './components/Marker/Marker';
import StepperList from './components/StepperList/StepperList';
import MapToolbar from './components/MapToolbar/MapToolbar';
import mapNames from '../../../../entities/map/constants';
import MarkerVectorLayerV2 from '../../../../shared/ui/GeoMap/MarkerVectorLayerV2';
import { createGarageIcon, createStorageIcon } from '../utils';
import groupPointsBySpiral from '../../../../shared/ui/GeoMap/utils/groupPointsBySpiral';

// Копипаст

const DEFAULT_COLOR = '#1976d2';

function PetrolDeliveryGeoMapEditorWidget(props) {
  const { logisticTask: logisticTaskProps, setSearchOrder } = props;
  const { isLoading, isError } = logisticTaskProps;

  const mapName = mapNames.logisticDelivery; // НУЖЕН ОТДЕЛЬНЫЙ ФЛАГ ДЛЯ КАРТЫ НОВОЙ

  const isFullscreen = useAppSelector((state) => state[`${reducerPath.fullScreenMap}`])[mapName];
  const logisticTask = useAppSelector((state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`]);

  const showTrackModeTrigger = React.useMemo(
    () => logisticTask?.data?.status === 'SOLVED' || logisticTask?.data?.status === 'ERROR',
    [logisticTask],
  );
  const selectedOrdersIds = React.useMemo(() => logisticTask?.selectedOrders, [logisticTask?.selectedOrders]);
  const selectedVehicles = React.useMemo(() => logisticTask?.selectedVehicles, [logisticTask?.selectedVehicles]);

  const ppsPoints = React.useMemo(
    () =>
      logisticTask?.data?.source?.allPpsList?.map((item) => ({
        ...item,
        coordinates: fromLonLat([item.longitude, item.latitude]),
      })) || [],
    [logisticTask?.data?.source?.allPpsList],
  );

  const garagePoints = React.useMemo(() => {
    const map = new Map();

    (logisticTask?.data?.source?.allTankers || []).forEach((tanker) => {
      const { garage } = tanker;
      if (garage && !map.has(garage.name)) {
        map.set(garage.name, {
          ...garage,
          coordinates: fromLonLat([garage.longitude, garage.latitude]),
        });
      }
    });

    return Array.from(map.values());
  }, [logisticTask?.data?.source?.allTankers]);

  const points = React.useMemo(() => {
    const newPoints = logisticTask?.data?.sourceWithSolution?.points;
    if (!newPoints) return null;
    const keys = Object.keys(newPoints);
    return { keys, values: newPoints };
  }, [logisticTask?.data?.sourceWithSolution?.points]);

  const adjustedPoints = React.useMemo(() => {
    if (!points || !points.values) return [];
    const markerArray = Object.values(points.values);
    return groupPointsBySpiral(markerArray, 5, 16);
  }, [points]);

  const lines = React.useMemo(
    () => logisticTask?.data?.sourceWithSolution?.lines,
    [logisticTask?.data?.sourceWithSolution?.lines],
  );
  const tracks = React.useMemo(
    () => logisticTask?.data?.sourceWithSolution?.tracks,
    [logisticTask?.data?.sourceWithSolution?.tracks],
  );
  const [extent, setExtent] = React.useState([]);
  const [trackMode, setTrackMode] = React.useState(false);

  const switchMode = () => setTrackMode((oldValue) => !oldValue);

  const combinedPointsArray = React.useMemo(() => {
    let combinedArray = null;
    if (logisticTask?.data?.sourceWithSolution?.points) {
      const keys = Object.keys(logisticTask?.data?.sourceWithSolution?.points);
      const initialPoints = logisticTask?.data?.sourceWithSolution?.points;
      combinedArray = keys?.reduce((prev, key) => {
        const point = initialPoints[key];
        if (point.coordinates) prev.push(point.coordinates);
        return prev;
      }, []);
    }
    return combinedArray;
  }, [logisticTask?.data?.sourceWithSolution?.points]);

  React.useEffect(() => {
    const savedExtent = window.localStorage.getItem('defaultExtent');
    let localExtent;
    if (!combinedPointsArray?.length) {
      localExtent = JSON.parse(savedExtent) || Extent.boundingExtent(DEFAULT_EXTENT);
    } else {
      localExtent = Extent.boundingExtent(combinedPointsArray);
    }
    setExtent(localExtent);
  }, [combinedPointsArray]);

  const saveExtent = React.useCallback((e) => {
    if (e?.map) {
      const currentExtent = e.map.getView().calculateExtent();
      window.localStorage.setItem('defaultExtent', JSON.stringify(currentExtent));
    }
  }, []);

  const saveZoom = React.useCallback(
    (map) => {
      saveExtent({ map });
    },
    [saveExtent],
  );

  const [drawingGeometryId, setDrawingGeometryId] = React.useState(null);

  const onClickDraw = (id) => setDrawingGeometryId(id);

  const dispatch = useDispatch();

  const updateCoords = (coords, id) => {
    const point = points.values[id];
    const newPoints = {
      ...points.values,
      [id]: {
        ...point,
        coordinates: coords,
        isEdited: true,
      },
    };
    const newData = JSON.parse(JSON.stringify(logisticTask?.data));
    const newEditedCoordinates = JSON.parse(JSON.stringify(logisticTask?.editedCoordinates));
    const srcOrder = newData.source.orderSourceList.find((o) => o.requestId === point.orderNumber);
    let solutionRouteOrder;
    newData.solution.routes?.find((r) => {
      const res = r.orders.find((o) => o.orderSource.requestId === point.orderNumber);
      if (res) {
        solutionRouteOrder = res;
      }
      return res;
    });
    const solutionUnallocatedOrder = newData.solution.notClosedRequests?.find((o) => o.requestId === point.orderNumber);
    const solutionOrder = solutionRouteOrder || solutionUnallocatedOrder;

    if (srcOrder) {
      const lonLat = coords ? toLonLat(coords) : [null, null];
      if (point.isLoading) {
        srcOrder.loadingLat = lonLat[1] || null;
        srcOrder.loadingLon = lonLat[0] || null;
        if (solutionOrder) {
          solutionOrder.orderSource = solutionOrder?.orderSource && {
            ...solutionOrder.orderSource,
            loadingLat: lonLat[1] || null,
            loadingLon: lonLat[0] || null,
          };
        }
        if (newEditedCoordinates?.[srcOrder.requestId]?.addressLoading) {
          newEditedCoordinates[srcOrder.requestId].addressLoading = null;
        }
      }
      if (point.isUnloading) {
        srcOrder.deliveryLat = lonLat[1] || null;
        srcOrder.deliveryLon = lonLat[0] || null;
        if (solutionOrder) {
          solutionOrder.orderSource = solutionOrder?.orderSource && {
            ...solutionOrder.orderSource,
            deliveryLat: lonLat[1] || null,
            deliveryLon: lonLat[0] || null,
          };
        }
        if (newEditedCoordinates?.[srcOrder.requestId]?.addressUnloading) {
          newEditedCoordinates[srcOrder.requestId].addressUnloading = null;
        }
      }
    }

    newData.sourceWithSolution = {
      ...transformData(newData).sourceWithSolution,
      points: newPoints,
    };

    dispatch(
      logisticTasksPetrolResourceActions.setData({
        [LogisticTasksEnum.data]: newData,
        [LogisticTasksEnum.editedCoordinates]: newEditedCoordinates,
      }),
    );
  };

  const handleClickPoint = (feature) => {
    const clickedPoint = points.values?.[feature.getId()];
    setSearchOrder(clickedPoint?.addresses?.deliveryAddress || '');
  };

  const onClickDelete = (curPoint) => {
    updateCoords(null, curPoint.id, curPoint);
  };

  const onStopDraw = (id, coords, curPoint) => {
    setDrawingGeometryId(null);
    updateCoords(coords, id, curPoint);
  };

  const onStopModify = (id, coords, curPoint) => {
    updateCoords(coords, id, curPoint);
  };

  React.useEffect(() => {
    if (isFullscreen) {
      window.scrollTo(0, 0);
    }
  }, [isFullscreen]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        <AlertTitle>Ошибка</AlertTitle>
        Ошибка при загрузке данных
      </Alert>
    );
  }

  return (
    <Box className={isFullscreen ? style.formFull : style.form}>
      <MapToolbar
        showTrackModeTrigger={showTrackModeTrigger}
        isFullscreen={isFullscreen}
        trackMode={trackMode}
        switchMode={switchMode}
      />
      <GeoMapWithTiles
        styles={isFullscreen ? style.geoMapFull : style.geoMap}
        extent={extent}
        isAnimateOnExtent
        name={mapName}
        isOffButtonFollowMode
        moveMapHandler={saveExtent}
        zoomHandler={saveZoom}
        hasScaleLine
      >
        {(trackMode ? tracks : lines)?.map((line) => {
          const isSelected = selectedVehicles.includes(line?.r?.tanker?.registrationNum);
          if (selectedVehicles?.length && !isSelected) return null;
          return (
            <LineVectorLayerV2
              key={line.id}
              zIndex={3}
              width={2}
              color={selectedOrdersIds?.length && points?.keys?.length >= 5 ? alpha(line.color, 0.4) : line.color}
              id={line.id}
              initialCoords={line.coordinates}
            />
          );
        })}
        {adjustedPoints.map((point) => {
          const isSelected = selectedVehicles.includes(point?.r?.tanker?.registrationNum);
          if (selectedVehicles?.length && !isSelected) return null;
          return (
            <Marker
              key={point.id}
              tooltipText={point.tooltipText}
              isStartDraw={drawingGeometryId === point.id}
              onStopDraw={(id, coords) => onStopDraw(id, coords, point)}
              onStopModify={(id, coords) => onStopModify(id, coords, point)}
              text={`${point.index}`}
              initialCoords={point.coordinates}
              id={point.id}
              selected={selectedOrdersIds}
              orderNumber={point.orderNumber}
              color={point.color}
              startIcon={point.startIcon}
              endIcon={point.endIcon}
              onClick={handleClickPoint}
            />
          );
        })}
        {ppsPoints.map((pps) => (
          <MarkerVectorLayerV2
            textHasOutline
            scaleTransformIntoPoint
            key={`${pps.name}-${pps.address}`}
            id={`${pps.name}-${pps.address}`}
            initialCoords={pps.coordinates}
            zIndex={10}
            tooltipText={pps.name ?? ''}
            src={createStorageIcon(DEFAULT_COLOR)}
            color={DEFAULT_COLOR}
          />
        ))}

        {garagePoints.map((garage) => (
          <MarkerVectorLayerV2
            textHasOutline
            scaleTransformIntoPoint
            key={`${garage.name}-${garage.address}`}
            id={`${garage.name}-${garage.address}`}
            initialCoords={garage.coordinates}
            zIndex={10}
            tooltipText={garage.name ?? ''}
            src={createGarageIcon(DEFAULT_COLOR)}
            color={DEFAULT_COLOR}
          />
        ))}
      </GeoMapWithTiles>
      <StepperList
        points={points}
        selectedOrdersIds={selectedOrdersIds}
        drawingGeometryId={drawingGeometryId}
        onClickDraw={onClickDraw}
        onClickDelete={onClickDelete}
      />
    </Box>
  );
}

PetrolDeliveryGeoMapEditorWidget.propTypes = {
  logisticTask: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }).isRequired,

  setSearchOrder: PropTypes.func.isRequired,
};

export default PetrolDeliveryGeoMapEditorWidget;
